import React, { useEffect, useState } from 'react';

import { ArrowLeft, Ellipsis, Loader } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CopyIcon, ExportIcon, InstagramIcon, LinkedInIcon, RemoveIcon, Website } from '#/assets/svgs';
import { SortableTableHeader } from '#/common/components';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Button,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from '#/components/ui';
import { useAppContext } from '#/context';
import { ROUTES } from '#/routes';
import { apiEndpoints, queryKeys, useDeleteMutation, useInfiniteScrollQuery, usePostMutation } from '#/services';
import { formatDate, MY_CONTACTS_TABLE_COL_GROUP, MY_CONTACTS_TABLE_HEADERS } from '#/utils';
import { DeleteContactsModal } from '..';

const MyContacts = () => {
    const navigate = useNavigate();

    const { user } = useAppContext();
    const exportCount = user?.exportEmployeeCount || 0;

    const [selectedContacts, setSelectedContacts] = useState([]);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [deleteContact, setDeleteContact] = useState(null);
    const [queryParams, setQueryParams] = useState({});
    const selectedCount = selectedContacts.length;
    const { ref, inView } = useInView();

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteScrollQuery({
        key: queryKeys?.MY_CONTACTS,
        url: apiEndpoints.GET_CONTACTS,
        params: queryParams,
    });
    const { mutate: removeContact, isLoading: deleteApiLoading } = useDeleteMutation({
        url: apiEndpoints.DELETE_CONTACT,
        queryKey: [queryKeys.USER, queryKeys.EMPLOYEES, queryKeys.MY_CONTACTS],
        successMessage: `${selectedCount > 1 ? 'Contacts' : 'Contact'} removed successfully!`,
        onSuccess: handleRemoveContactSuccess,
    });

    const { mutate: exportContacts, isLoading: exportApiLoading } = usePostMutation({
        url: apiEndpoints.EXPORT_CSV,
        queryKey: [queryKeys.USER, queryKeys.MY_CONTACTS],
        onSuccess: handleExportSuccess,
    });

    function handleRemoveContactSuccess() {
        setSelectedContacts([]);
        setOpenConfirmDialog(false);
        setDeleteContact(null);
    }
    function handleExportSuccess(resp) {
        setSelectedContacts([]);
        let csvString;
        if (typeof resp === 'string') {
            csvString = resp;
        } else if (Array.isArray(resp)) {
            csvString = resp.join('');
        } else if (resp !== null && typeof resp === 'object' && Object.keys(resp).every(k => /^\d+$/.test(k))) {
            csvString = Object.values(resp).join('');
        } else {
            csvString = String(resp);
        }
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'contacts.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

    const contactsList = data?.pages?.flatMap(page => page?.results) ?? [];

    const confirmRemoveContact = ids => {
        setPendingDeleteIds(ids);
        setDeleteContact(null);
        setOpenConfirmDialog(true);
    };

    const handleRemoveSingleContact = (id, name) => () => {
        confirmRemoveContact([id]);
        setDeleteContact(name);
    };
    const handleNavigateBack = () => {
        navigate(ROUTES.HOME);
    };

    const allSelected = contactsList?.length > 0 && selectedCount === contactsList.length;

    const handleSelectAll = checked => {
        if (checked) {
            setSelectedContacts(contactsList.map(c => c.id));
        } else {
            setSelectedContacts([]);
        }
    };

    const handleRemoveSelected = () => {
        confirmRemoveContact(selectedContacts);
    };

    const handleExportSelected = () => {
        exportContacts({
            payload: { contact_ids: selectedContacts },
        });
    };

    const handleConfirmDelete = () => {
        if (pendingDeleteIds?.length > 0) {
            removeContact({ payload: { contact_ids: pendingDeleteIds } });
        }
    };
    return (
        <>
            <div className='py-5 bg-white rounded-lg shadow-md w-[calc(100dvw-120px)]'>
                <div className='flex justify-between'>
                    <div className='flex items-center gap-3 px-5'>
                        <div className='flex items-center gap-2 '>
                            <div className='cursor-pointer' onClick={handleNavigateBack}>
                                <ArrowLeft />
                            </div>
                            <h1 className='font-cormorant font-semibold text-2xl text-textPrimary '>My Contacts</h1>
                        </div>
                        <h4 className='bg-gray100 rounded-lg border-none px-3 py-2 text-sm font-light text-gray600'>
                            Contacts to Export: <span className='text-textPrimary font-normal'>{exportCount} left</span>
                        </h4>
                    </div>
                    <div className='flex flex-row items-center gap-2 px-5'>
                        {selectedCount > 0 && (
                            <Button
                                variant='outline'
                                className='text-sm font-medium text-gray500 px-4 py-3'
                                onClick={handleRemoveSelected}
                                disabled={deleteApiLoading}
                            >
                                <RemoveIcon /> Remove ({selectedCount})
                            </Button>
                        )}
                        <Button
                            className={`text-sm font-medium text-white ${selectedCount === 0 && 'bg-gray300 pointer-events-none'}`}
                            onClick={handleExportSelected}
                            disabled={exportApiLoading}
                        >
                            <ExportIcon /> Export ({selectedCount})
                        </Button>
                    </div>
                </div>

                <div className='pt-5'>
                    <div className='h-[calc(100dvh-210px)] overflow-auto'>
                        <Table className='w-full table-fixed'>
                            {MY_CONTACTS_TABLE_COL_GROUP}
                            <TableHeader className={'sticky top-0 z-10'}>
                                <SortableTableHeader
                                    headers={MY_CONTACTS_TABLE_HEADERS}
                                    setQueryParams={setQueryParams}
                                    allSelected={allSelected}
                                    onSelectAll={handleSelectAll}
                                />
                            </TableHeader>
                            <TableBody className='w-full '>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className='h-[300px] text-center'>
                                            <Loader className='mx-auto animate-spin text-muted-foreground' />
                                        </TableCell>
                                    </TableRow>
                                ) : contactsList?.length > 0 ? (
                                    contactsList?.map(item => {
                                        const { id, employee, exportedAt, created } = item || {};
                                        const {
                                            firstName,
                                            lastName,
                                            title,
                                            email,
                                            linkedinUrl,
                                            websiteUrl,
                                            instagramUrl,
                                            profilePicture,
                                            company,
                                        } = employee || {};

                                        const { name: companyName } = company || {};
                                        return (
                                            <TableRow key={id} className='h-[72px] hover:bg-transparent cursor-default'>
                                                <TableCell className='h-[72px] flex items-center justify-start gap-3 mx-4'>
                                                    <Checkbox
                                                        className='w-4 h-4'
                                                        checked={selectedContacts.includes(id)}
                                                        onCheckedChange={() => {
                                                            setSelectedContacts(prev =>
                                                                prev.includes(id)
                                                                    ? prev.filter(i => i !== id)
                                                                    : [...prev, id]
                                                            );
                                                        }}
                                                    />

                                                    <Avatar className='w-10 h-10'>
                                                        <AvatarImage src={profilePicture} alt={firstName} />
                                                        <AvatarFallback className='bg-gray200 text-gray500'>
                                                            {firstName?.charAt(0)}
                                                            {lastName?.charAt(0)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className='flex flex-col'>
                                                        <span className='whitespace-normal break-words max-w-xs text-gray900 text-sm font-medium capitalize'>
                                                            {firstName} {lastName}
                                                        </span>
                                                        {email && (
                                                            <div className='flex flex-row items-center gap-2'>
                                                                <span className='text-sm text-gray500 font-normal max-w-[200px] truncate'>
                                                                    {email}
                                                                </span>
                                                                <CopyIcon
                                                                    className='cursor-pointer'
                                                                    onClick={() => {
                                                                        const copyEmail = email || '';
                                                                        navigator.clipboard
                                                                            .writeText(copyEmail)
                                                                            .then(() => {
                                                                                toast.success(
                                                                                    'Email copied to clipboard!'
                                                                                );
                                                                            });
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className='text-sm pl-4 font-normal text-gray500 whitespace-normal break-words'>
                                                    {companyName}
                                                </TableCell>
                                                <TableCell className='text-sm pl-4 font-normal text-gray500 whitespace-normal break-words'>
                                                    {title || '-'}
                                                </TableCell>
                                                <TableCell className='text-sm pl-4 font-normal text-gray500 whitespace-normal break-words'>
                                                    {formatDate(created) || '-'}
                                                </TableCell>
                                                <TableCell className='text-sm pl-4 font-normal text-gray500 whitespace-normal break-words'>
                                                    {formatDate(exportedAt) || '-'}
                                                </TableCell>
                                                <TableCell className='pl-4' onClick={e => e.stopPropagation()}>
                                                    <div className='flex items-center gap-2 '>
                                                        {websiteUrl && (
                                                            <a
                                                                href={websiteUrl}
                                                                target='_blank'
                                                                rel='noopener noreferrer'
                                                                // className='flex  items-center justify-center'
                                                            >
                                                                <Website />
                                                            </a>
                                                        )}
                                                        {linkedinUrl && (
                                                            <a
                                                                href={linkedinUrl}
                                                                target='_blank'
                                                                rel='noopener noreferrer'
                                                                // className='flex m-0 p-0 items-center justify-center '
                                                            >
                                                                <LinkedInIcon />
                                                            </a>
                                                        )}
                                                        {instagramUrl && (
                                                            <a
                                                                href={instagramUrl}
                                                                target='_blank'
                                                                rel='noopener noreferrer'
                                                                // className='inline-flex items-center justify-center'
                                                            >
                                                                <InstagramIcon />
                                                            </a>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className='p-0'>
                                                    <Button
                                                        variant='outline'
                                                        className='text-sm font-medium bg-gray100 border-none text-gray500 px-4 py-3'
                                                        onClick={handleRemoveSingleContact(id, firstName)}
                                                    >
                                                        <RemoveIcon /> Remove
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow className='border-none hover:bg-transparent'>
                                        <TableCell colSpan={5} className='h-[300px] text-center text-gray-400'>
                                            No contacts found
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow className='pointer-events-none'>
                                    <TableCell colSpan={5}>
                                        <div ref={ref} id='sentinel' className='py-6 flex justify-center text-center'>
                                            {isFetchingNextPage && <Ellipsis className='animate-ping' />}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            <DeleteContactsModal
                open={openConfirmDialog}
                onClose={setOpenConfirmDialog}
                onConfirm={handleConfirmDelete}
                contactCount={pendingDeleteIds?.length}
                deleteContact={deleteContact}
                isLoading={deleteApiLoading}
            />
        </>
    );
};

export { MyContacts };
